// eslint-disable-next-line jira/wrm/no-load-bridge
import { loadBridge } from '@atlassian/jira-common-bridge/src';
import log from '@atlassian/jira-common-util-logging/src/log';
import { getAnalyticsWebClient } from '@atlassian/jira-product-analytics-web-client';

import type { EmbeddedProduct, EmbeddedProductResolver } from './types';

export const ERROR_LOCATION = 'jira.analytics.embedded-product';

let cachedResolver: Promise<EmbeddedProductResolver>;

export const loadAndCacheResolver = () => {
	if (cachedResolver) {
		return cachedResolver;
	}

	const wrmKeys = [
		'wrc!jira.webresources:top-same-origin-window',
		'wrc!com.atlassian.jira.jira-client-analytics-plugin:analytics-web-client',
	];

	// eslint-disable-next-line jira/wrm/no-load-bridge
	cachedResolver = loadBridge<EmbeddedProductResolver>({
		name: 'jira/analytics/embedded-product-resolver',
		wrmKeys,
		shouldLogError: false,
	})
		.then((embeddedProductResolver) => {
			const updateValue = (embeddedProduct: EmbeddedProduct | false) => {
				// Set embedded product fn to reset the "track" interval (check analytics-web-client sources for more details).
				// User has to be at least active for 2 seconds before we can submit the track event.
				embeddedProduct
					? getAnalyticsWebClient().setEmbeddedProduct(embeddedProduct)
					: getAnalyticsWebClient().clearEmbeddedProduct();
			};

			updateValue(embeddedProductResolver.get());
			embeddedProductResolver.onChange(updateValue);

			return embeddedProductResolver;
		})
		.catch((e) => {
			log.safeErrorWithoutCustomerData(
				ERROR_LOCATION,
				'Cannot get embedded product resolver via bridge',
				e,
			);
			throw e;
		});
	/* eslint-enable jira/wrm/no-load-bridge */

	return cachedResolver;
};

export const set = (embeddedProduct: EmbeddedProduct) =>
	loadAndCacheResolver().then((resolver) => resolver.set(embeddedProduct));

export const clear = () => loadAndCacheResolver().then((resolver) => resolver.clear());

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setCachedResolverForTestPurposesOnly = (value: any) => {
	cachedResolver = value;
};
